import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Save,
  MessageCircle,
  Mail,
  Eye,
  RotateCcw,
  CheckCircle2,
} from "lucide-react";

/**
 * KAI · Editor de plantillas de mensajes (WhatsApp + Email).
 *
 * - GET  /api/kai/message-templates   → returns merged defaults + overrides.
 * - PUT  /api/kai/message-templates   → sudo-gated (PIN maestro).
 *
 * Live MessagePreview: renders placeholders ({{nombre}}, {{documento}},
 * {{codigo_expediente}}, {{enlace}}, {{telefono}}) with a mock student so
 * the admin can see the final wording BEFORE saving.
 */

const WHATSAPP_KEYS = [
  { key: "default",                    label: "Plantilla por defecto",             hint: "Se usa para cualquier documento sin plantilla específica." },
  { key: "autorizacion_dgt",           label: "Autorización DGT" },
  { key: "mandato_sepa",               label: "Mandato SEPA" },
  { key: "justificante_pago",          label: "Justificante de pago" },
  { key: "solicitud_dgt_nueva",        label: "Solicitud DGT nueva" },
  { key: "solicitud_traslado",         label: "Solicitud de traslado" },
  { key: "proteccion_datos",           label: "Protección de datos / LOPD" },
  { key: "autorizacion_tramitacion",   label: "Autorización tramitación" },
  { key: "bulk_upcoming",              label: "Envío agrupado — Próximas sesiones (WhatsApp)", hint: "Se envía desde el botón 'Enviar próximas sesiones'. Placeholders: {{nombre}}, {{bloque_clases}}, {{bloque_examen}}, {{enlace}}." },
];

const PLACEHOLDER_HELP = [
  { key: "{{nombre}}",             desc: "Nombre del alumno" },
  { key: "{{documento}}",          desc: "Nombre legible del documento" },
  { key: "{{codigo_expediente}}",  desc: "Código de expediente (ej: 140N)" },
  { key: "{{enlace}}",             desc: "URL del enlace de firma / gestión / confirmación" },
  { key: "{{telefono}}",           desc: "Teléfono del alumno" },
  { key: "{{bloque_clases}}",      desc: "Envío agrupado: lista de prácticas (📚 …)" },
  { key: "{{bloque_examen}}",      desc: "Envío agrupado: bloque 🚗 EXAMEN PRÁCTICO DGT si aplica" },
];

const DEMO_CONTEXT = {
  nombre: "Ana",
  documento: "Autorización DGT",
  codigo_expediente: "140N",
  enlace: "https://natur.es/confirm?student=demo-123",
  telefono: "+34 600 123 456",
  bloque_clases:
    "📚 PRÁCTICAS DE CIRCULACIÓN\n" +
    "3 sesiones programadas\n" +
    "para un total de 6 clases de 45 minutos c/u\n" +
    "✅ Lunes 03/08 - 08:00 a 09:30\n" +
    "✅ Martes 04/08 - 11:15 a 12:45\n" +
    "✅ Jueves 06/08 - 09:30 a 11:00\n" +
    "\n",
  bloque_examen:
    "y un posible 🚗 EXAMEN PRÁCTICO DGT\n" +
    "Fecha prevista:\n" +
    "18/08/2026\n" +
    "Estado:\n" +
    "⏳ No confirmado aún por DGT\n" +
    "La DGT suele comunicar la confirmación de convocatoria entre 3 y 7 días antes del examen y la hora exacta aproximadamente entre 48 y 72 horas antes.\n" +
    "\n",
};

function renderTemplate(tpl, ctx) {
  if (!tpl) return "";
  let out = String(tpl);
  for (const [k, v] of Object.entries(ctx || {})) {
    out = out.split(`{{${k}}}`).join(String(v ?? ""));
  }
  return out;
}


function MessagePreview({ tpl, ctx, testid = "message-preview" }) {
  const rendered = useMemo(() => renderTemplate(tpl, ctx), [tpl, ctx]);
  const missingPlaceholders = useMemo(() => {
    const matches = String(tpl || "").match(/\{\{[a-z_]+\}\}/g) || [];
    return matches
      .map((m) => m.slice(2, -2))
      .filter((k) => !(k in (ctx || {})));
  }, [tpl, ctx]);

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-3 space-y-2" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-700 font-bold">
        <Eye className="h-3 w-3" />
        Previsualización
      </div>
      <div
        className="rounded-lg bg-gradient-to-b from-emerald-50 to-white p-3 text-sm text-neutral-800 whitespace-pre-wrap leading-relaxed border border-emerald-100"
        data-testid={`${testid}-body`}
      >
        {rendered || <span className="text-neutral-300 italic">Escribe una plantilla…</span>}
      </div>
      {missingPlaceholders.length > 0 && (
        <p className="text-[10px] text-amber-700">
          ⚠️ Placeholders desconocidos:{" "}
          <span className="font-mono">{missingPlaceholders.join(", ")}</span>
        </p>
      )}
    </div>
  );
}


function WhatsAppTemplateRow({ item, value, defaultValue, onChange, onSave, saving }) {
  const dirty = value !== defaultValue;
  return (
    <div
      className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 space-y-2"
      data-testid={`msg-tpl-wa-${item.key}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <MessageCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" />
        <h4 className="text-sm font-semibold text-neutral-900">{item.label}</h4>
        {dirty && (
          <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5">
            Sin guardar
          </span>
        )}
        <button
          type="button"
          onClick={() => onChange(defaultValue)}
          disabled={saving || !dirty}
          className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-neutral-500 hover:text-orange-600 disabled:opacity-40"
          data-testid={`msg-tpl-wa-${item.key}-reset`}
        >
          <RotateCcw className="h-3 w-3" /> Default
        </button>
      </div>
      {item.hint && (
        <p className="text-[11px] text-neutral-500">{item.hint}</p>
      )}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={saving}
        rows={3}
        className="w-full rounded-lg border border-neutral-300 bg-white p-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-neutral-100"
        data-testid={`msg-tpl-wa-${item.key}-input`}
      />
      <MessagePreview
        tpl={value}
        ctx={DEMO_CONTEXT}
        testid={`msg-tpl-wa-${item.key}-preview`}
      />
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !dirty}
          data-testid={`msg-tpl-wa-${item.key}-save`}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-40"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Guardar
        </button>
      </div>
    </div>
  );
}


function EmailTemplateBlock({ subject, body, defaults, onChange, onSave, saving, resendReady }) {
  const dirtySubject = subject !== defaults.subject;
  const dirtyBody = body !== defaults.body;
  const dirty = dirtySubject || dirtyBody;
  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 space-y-3" data-testid="msg-tpl-email">
      <div className="flex items-center gap-2 flex-wrap">
        <Mail className="h-4 w-4 text-blue-600" />
        <h4 className="text-sm font-semibold text-neutral-900">Correo electrónico (Resend)</h4>
        {!resendReady && (
          <span className="inline-flex items-center rounded-full bg-neutral-200 text-neutral-700 text-[10px] font-bold px-2 py-0.5">
            RESEND API MOCKED
          </span>
        )}
        {dirty && (
          <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5">
            Sin guardar
          </span>
        )}
      </div>

      <div>
        <label className="text-[11px] font-semibold text-neutral-600 mb-1 block">Asunto</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => onChange({ subject: e.target.value, body })}
          disabled={saving}
          className="w-full h-9 px-2 rounded-lg border border-neutral-300 bg-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-neutral-100"
          data-testid="msg-tpl-email-subject"
        />
      </div>

      <div>
        <label className="text-[11px] font-semibold text-neutral-600 mb-1 block">Cuerpo del email</label>
        <textarea
          value={body}
          onChange={(e) => onChange({ subject, body: e.target.value })}
          disabled={saving}
          rows={6}
          className="w-full rounded-lg border border-neutral-300 bg-white p-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-neutral-100"
          data-testid="msg-tpl-email-body"
        />
      </div>

      <MessagePreview tpl={body} ctx={DEMO_CONTEXT} testid="msg-tpl-email-preview" />

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => onChange({ subject: defaults.subject, body: defaults.body })}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-1 h-8 px-2 rounded-lg text-[11px] font-semibold text-neutral-500 hover:text-orange-600 disabled:opacity-40"
          data-testid="msg-tpl-email-reset"
        >
          <RotateCcw className="h-3 w-3" /> Defaults
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving || !dirty}
          data-testid="msg-tpl-email-save"
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold disabled:opacity-40"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Guardar
        </button>
      </div>
    </div>
  );
}


export default function MessageTemplatesEditor() {
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState({ whatsapp: {}, email: {} });
  const [defaults, setDefaults] = useState({ whatsapp: {}, email: {} });
  const [resendReady, setResendReady] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/kai/message-templates");
      const tpl = data?.templates || { whatsapp: {}, email: {} };
      setTemplates({
        whatsapp: { ...(tpl.whatsapp || {}) },
        email: { ...(tpl.email || {}) },
      });
      // The API merges defaults into templates, so we snapshot as our
      // "default" baseline (used to detect dirty state & offer reset).
      setDefaults({
        whatsapp: { ...(tpl.whatsapp || {}) },
        email: { ...(tpl.email || {}) },
      });
      setResendReady(Boolean(data?.resend_ready));
    } catch (e) {
      toast.error(formatApiError(e, "No se pudieron cargar las plantillas de mensajes"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveOne = useCallback(async (channel, key, value) => {
    setSaving(true);
    try {
      await api.put("/kai/message-templates", { channel, key, value });
      toast.success("Plantilla guardada");
      setDefaults((prev) => ({
        ...prev,
        [channel]: { ...prev[channel], [key]: value },
      }));
    } catch (e) {
      toast.error(formatApiError(e, "No se pudo guardar la plantilla"));
    } finally {
      setSaving(false);
    }
  }, []);

  const setWhatsappValue = useCallback((key, value) => {
    setTemplates((prev) => ({
      ...prev,
      whatsapp: { ...prev.whatsapp, [key]: value },
    }));
  }, []);

  const setEmailValue = useCallback(({ subject, body }) => {
    setTemplates((prev) => ({
      ...prev,
      email: { ...prev.email, default_subject: subject, default_body: body },
    }));
  }, []);

  if (loading) {
    return (
      <section className="card-surface p-6 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
      </section>
    );
  }

  return (
    <section
      className="card-surface p-4 sm:p-6 space-y-5"
      data-testid="message-templates-editor"
    >
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0">
          <MessageCircle className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold leading-tight">Plantillas de mensajes</h2>
          <p className="text-xs text-neutral-500">
            Configura los textos que se envían por WhatsApp y por email. Cada campo se
            guarda de forma independiente y requiere PIN maestro.
          </p>
        </div>
      </div>

      {/* Placeholder cheatsheet */}
      <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3">
        <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-500 mb-2">
          Placeholders disponibles
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-[11px]">
          {PLACEHOLDER_HELP.map((p) => (
            <div key={p.key} className="flex gap-2">
              <code className="font-mono text-orange-600 font-bold whitespace-nowrap">{p.key}</code>
              <span className="text-neutral-600 truncate">{p.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* WhatsApp templates */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-emerald-600" />
          <h3 className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">
            WhatsApp
          </h3>
        </div>
        {WHATSAPP_KEYS.map((item) => {
          const val = templates.whatsapp?.[item.key] ?? "";
          const def = defaults.whatsapp?.[item.key] ?? "";
          return (
            <WhatsAppTemplateRow
              key={item.key}
              item={item}
              value={val}
              defaultValue={def}
              onChange={(v) => setWhatsappValue(item.key, v)}
              onSave={() => saveOne("whatsapp", item.key, val)}
              saving={saving}
            />
          );
        })}
      </div>

      {/* Email */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-blue-600" />
          <h3 className="text-[11px] uppercase tracking-wider font-bold text-neutral-500">
            Email
          </h3>
        </div>
        <EmailTemplateBlock
          subject={templates.email?.default_subject || ""}
          body={templates.email?.default_body || ""}
          defaults={{
            subject: defaults.email?.default_subject || "",
            body: defaults.email?.default_body || "",
          }}
          onChange={setEmailValue}
          onSave={async () => {
            await saveOne("email", "default_subject", templates.email?.default_subject || "");
            await saveOne("email", "default_body", templates.email?.default_body || "");
          }}
          saving={saving}
          resendReady={resendReady}
        />
      </div>

      <div className="text-[11px] text-neutral-500 italic border-t border-neutral-100 pt-3 flex items-start gap-1">
        <CheckCircle2 className="h-3 w-3 mt-0.5 text-emerald-600 shrink-0" />
        <span>
          KAI respeta siempre <code>No contactar</code>, <code>Baja voluntaria</code>,{" "}
          <code>Pendiente decisión</code> y el máximo de recordatorios configurado. Estas
          plantillas <b>solo</b> definen el TEXTO — nunca invalidan las reglas de contacto.
        </span>
      </div>
    </section>
  );
}
